import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { License } from 'types/licenses.types';
import useMapProductNames from './use-map-product-names';

interface Props {
  open: boolean;
  loading?: boolean;
  license: License | null;

  handleDeleteLicense(): void;

  handleClose(): void;
}

const DeleteLicenseDialog: React.FC<Props> = ({ open, loading, license, handleDeleteLicense, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const mapProductName = useMapProductNames();
  const licenseName = `${mapProductName(license?.product ?? '') ?? ''}`;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('deleteLicense')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>
            <Trans i18nKey={'deleteLicenseWarning1'} values={{ licenseName }}>
              Should the license <strong>licenseName</strong> really be deleted?
            </Trans>
          </Typography>
          <Typography className={cx(classes.text, 'highlight')}>{t('deleteLicenseWarning2')}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('deleteLicense')} size={'small'} border={'rounded'} color={'error'} action={handleDeleteLicense} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.error.main}, ${theme.palette.error.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.warning': {
      color: theme.palette.warning.main,
      fontWeight: 'bold',
    },
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default DeleteLicenseDialog;
